/**
 * Event batching and sending module
 */

import type { SessionBatch, RRWebEvent } from './types';
import { sessionState } from './state.ts';
import { MAX_PAYLOAD_SIZE, MIN_BATCH_EVENT_COUNT, KEEPALIVE_SAFE_BYTES, PENDING_CHUNKS_MAX_BYTES } from './constants.ts';
import { chunkEvents } from './chunk.ts';
import { redactUrl } from './redact.ts';

/**
 * Build payload for sending to backend
 * Extracted for testability
 */
export function buildPayload(
  eventsToSend: RRWebEvent[],
  isFinalSend: boolean
): SessionBatch {
  const sessionEndTime = Date.now();
  const sessionDuration = Math.round((sessionEndTime - sessionState.sessionStartTime) / 1000);

  return {
    sessionId: sessionState.sessionId,
    projectId: sessionState.projectId,
    visitorId: sessionState.visitorId,
    referrer: sessionState.referrer,
    landingUrl: sessionState.landingUrl,
    url: redactUrl(window.location.href),
    userAgent: navigator.userAgent,
    deviceType: sessionState.isMobile ? "mobile" : "desktop",
    timestamp: new Date().toISOString(),
    duration: sessionDuration,
    errorCount: sessionState.errorCount,
    networkErrorCount: sessionState.networkErrorCount,
    events: eventsToSend,
    startTime: new Date(sessionState.sessionStartTime).toISOString(),
    endTime: new Date(sessionEndTime).toISOString(),
    eventCount: eventsToSend.length,
    isFinalBatch: isFinalSend,
    telemetry: sessionState.telemetryEvents
  };
}

/**
 * Build a minimal final payload for reliable sendBeacon delivery.
 * All rrweb events were already sent in periodic batches.
 * Includes accumulated telemetry (vitals, errors) that may not have been
 * sent yet if the last periodic batch was skipped due to low event count.
 */
export function buildFinalPayload(): SessionBatch {
  const sessionEndTime = Date.now();
  const sessionDuration = Math.round((sessionEndTime - sessionState.sessionStartTime) / 1000);
  const telemetryToSend = sessionState.telemetryEvents;

  return {
    sessionId: sessionState.sessionId,
    projectId: sessionState.projectId,
    visitorId: sessionState.visitorId,
    referrer: sessionState.referrer,
    landingUrl: sessionState.landingUrl,
    url: redactUrl(window.location.href),
    userAgent: navigator.userAgent,
    deviceType: sessionState.isMobile ? "mobile" : "desktop",
    timestamp: new Date().toISOString(),
    duration: sessionDuration,
    errorCount: sessionState.errorCount,
    networkErrorCount: sessionState.networkErrorCount,
    events: [],
    startTime: new Date(sessionState.sessionStartTime).toISOString(),
    endTime: new Date(sessionEndTime).toISOString(),
    eventCount: 0,
    isFinalBatch: true,
    telemetry: telemetryToSend
  };
}

/**
 * Check if batch should be sent
 * Extracted for testability
 */
export function shouldSendBatch(isFinalSend: boolean): boolean {
  // Always send final batches, even if empty — signals session end to backend
  if (isFinalSend) {
    return true;
  }

  if (sessionState.events.length === 0) {
    return false;
  }

  // Calculate how many events were accumulated in the current batch window
  const currentBatchEventCount = sessionState.events.length - sessionState.currentBatchStartIndex;

  // Only send if the current batch window has at least MIN_BATCH_EVENT_COUNT events
  if (currentBatchEventCount < MIN_BATCH_EVENT_COUNT) {
    // Update the start index for the next batch window
    sessionState.currentBatchStartIndex = sessionState.events.length;
    return false;
  }

  return true;
}

/**
 * Estimate JSON payload size in bytes without full serialization.
 * Uses a fast heuristic: sample 10% of events, measure average size, extrapolate.
 */
function estimatePayloadSize(events: RRWebEvent[]): number {
  if (events.length === 0) return 0;
  if (events.length <= 100) {
    // Small batch: just measure directly
    return new Blob([JSON.stringify(events)]).size;
  }
  // Large batch: sample ~10% with a minimum of 20 events
  const sampleSize = Math.min(Math.max(20, Math.floor(events.length * 0.1)), 200);
  const step = Math.floor(events.length / sampleSize);
  let sampleBytes = 0;
  let count = 0;
  for (let i = 0; i < events.length; i += step) {
    sampleBytes += JSON.stringify(events[i]).length;
    count++;
    if (count >= sampleSize) break;
  }
  return Math.round((sampleBytes / count) * events.length);
}

/**
 * Flush aggregated per-endpoint network stats into telemetry events
 * so they are included in the next batch payload.
 */
function flushNetworkStatsToTelemetry(): void {
  const stats = Object.values(sessionState.networkEndpointStats);
  if (stats.length === 0) return;

  for (const s of stats) {
    const evt = {
      type: 'network_stats' as const,
      subtype: 'endpoint_summary',
      timestamp: Date.now(),
      data: {
        pattern: s.pattern,
        method: s.method,
        requestCount: s.requestCount,
        errorCount: s.errorCount,
        averageResponseTime: Math.round(s.totalResponseTime / s.requestCount),
      },
    };
    sessionState.telemetryEvents.push(evt);
  }

  // Clear aggregated stats so we don't double-send
  sessionState.networkEndpointStats = {};
}

/**
 * Send compressed session data to backend
 * This is called periodically AND when the session ends
 */
export function sendEventBatch(isFinalSend: boolean, endReason?: string): void {
  flushNetworkStatsToTelemetry();

  if (!shouldSendBatch(isFinalSend)) {
    return;
  }

  let eventsToSend: RRWebEvent[];

  if (isFinalSend) {
    // Final sends take ALL remaining events — the 500KB split below is for
    // periodic sends only. Splitting a final send would abandon the unsent
    // half: the page is unloading, so there is no "next batch". Chunking in
    // sendFinalBatch handles payload sizing instead.
    eventsToSend = sessionState.events;
    sessionState.events = [];
    sessionState.currentBatchStartIndex = 0;
    sendFinalBatch(eventsToSend, endReason);
    return;
  }

  // Periodic send: grab events, splitting if needed to avoid 413 errors
  eventsToSend = sessionState.events;
  const payloadSize = estimatePayloadSize(eventsToSend);

  if (payloadSize > MAX_PAYLOAD_SIZE && eventsToSend.length > 50) {
    // Send half now, keep rest for next batch
    const halfIndex = Math.floor(eventsToSend.length / 2);
    eventsToSend = sessionState.events.slice(0, halfIndex);
    sessionState.events = sessionState.events.slice(halfIndex);
    // Reset batch start index for remaining events
    sessionState.currentBatchStartIndex = 0;
  } else {
    // Clear events array immediately so we don't send duplicates
    sessionState.events = [];
    // Reset batch start index since we're sending everything
    sessionState.currentBatchStartIndex = 0;
  }

  const eventCount = eventsToSend.length;

  const payload = buildPayload(eventsToSend, isFinalSend);

  // Clear telemetry events after building payload to prevent unbounded growth
  sessionState.telemetryEvents = [];

  // Use fetch for periodic sends (no keepalive needed)
  fetch(sessionState.backendUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    mode: "cors",
    credentials: "omit",
  })
    .then(function (response) {
      if (!response.ok) {
        throw new Error(
          "HTTP " + response.status + " - " + response.statusText
        );
      }
      sessionState.totalEventsSent += eventCount;
      return response.json();
    })
    .then(function () {
      // Batch sent successfully
    })
    .catch(function (error: Error) {
      // Put events back for retry (only if not final send)
      if (!isFinalSend) {
        sessionState.events = eventsToSend.concat(sessionState.events);
        // Reset batch start index when requeueing
        sessionState.currentBatchStartIndex = 0;
      }
    });
}

/**
 * Final send: deliver all remaining events as chunked keepalive fetches,
 * plus a minimal backup beacon as the session-end signal.
 *
 * Browsers cap the total body size of in-flight keepalive requests (~64KB in
 * Chrome, shared with sendBeacon), and a final payload containing the rrweb
 * full snapshot routinely exceeds that — so events are split into chunks that
 * each fit safely under the budget. Only the last chunk carries
 * isFinalBatch=true; ALL chunks carry isFinalChunk=true so the backend
 * finalizes/merges correctly regardless of arrival order.
 *
 * A chunk whose keepalive fetch is rejected (over budget, network failure at
 * unload) is persisted to a localStorage retry queue and flushed on the
 * visitor's next page load via plain fetch (no keepalive size cap).
 */
function sendFinalBatch(eventsToSend: RRWebEvent[], endReason?: string): void {
  const chunked = chunkEvents(eventsToSend, KEEPALIVE_SAFE_BYTES);
  // Always send at least one chunk so the backend sees the session end.
  const chunks = chunked.length > 0 ? chunked : [[]];

  const totalChunks = chunks.length;
  chunks.forEach(function (chunkEventList, index) {
    const isLastChunk = index === totalChunks - 1;
    const payload = buildPayload(chunkEventList, isLastChunk);
    payload.isFinalChunk = true;
    // Telemetry rides the last chunk only — buildPayload attaches the same
    // telemetryEvents buffer to every payload it builds.
    if (!isLastChunk) {
      payload.telemetry = [];
    }
    const body = JSON.stringify(payload);

    fetch(sessionState.backendUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body,
      keepalive: true,
      mode: "cors",
      credentials: "omit",
    }).catch(function () {
      enqueuePendingChunk(body);
    });
  });

  // Backup: minimal beacon to signal session end
  const finalPayload = buildFinalPayload();
  const finalPayloadStr = JSON.stringify(finalPayload);
  const beaconSize = new Blob([finalPayloadStr]).size;

  console.log(`[Session Replay] Finalizing session, events=${eventsToSend.length}, chunks=${totalChunks}, beacon size: ${beaconSize} bytes, reason: ${endReason || 'unknown'}`);

  if (navigator.sendBeacon) {
    // text/plain avoids the CORS preflight that credentialed beacons fail
    const blob = new Blob([finalPayloadStr], { type: "text/plain" });
    const success = navigator.sendBeacon(sessionState.backendUrl, blob);
    console.log(`[Session Replay] sendBeacon result: ${success}`);
  }

  // Clear telemetry since we sent it in the final chunk
  sessionState.telemetryEvents = [];
}

/**
 * localStorage retry queue for final-send chunks that failed to deliver
 * (keepalive budget rejection, network failure at unload). The backend
 * merges late arrivals into the finalized replay archive.
 */
const PENDING_CHUNKS_KEY = "__tk_pending_chunks";

function readPendingChunks(): string[] {
  try {
    const raw = localStorage.getItem(PENDING_CHUNKS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePendingChunks(queue: string[]): void {
  try {
    if (queue.length === 0) {
      localStorage.removeItem(PENDING_CHUNKS_KEY);
    } else {
      localStorage.setItem(PENDING_CHUNKS_KEY, JSON.stringify(queue));
    }
  } catch {
    // Storage blocked or full — nothing more we can do.
  }
}

function enqueuePendingChunk(body: string): void {
  const queue = readPendingChunks();
  queue.push(body);
  // Bound total size; drop oldest first.
  let total = queue.reduce((sum, item) => sum + item.length, 0);
  while (queue.length > 1 && total > PENDING_CHUNKS_MAX_BYTES) {
    total -= queue[0].length;
    queue.shift();
  }
  writePendingChunks(queue);
}

/**
 * Flush queued final chunks from previous page loads. Sequential to avoid
 * bursting at startup; stops at the first failure (retried on next load).
 * Fire-and-forget — never blocks or breaks tracker init.
 */
export function flushPendingChunks(): void {
  const queue = readPendingChunks();
  if (queue.length === 0) return;

  const remaining = queue.slice();
  const sendNext = function (): void {
    const body = remaining[0];
    if (body === undefined) {
      writePendingChunks([]);
      return;
    }
    fetch(sessionState.backendUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body,
      mode: "cors",
      credentials: "omit",
    }).then(function (response) {
      if (!response.ok) {
        throw new Error("HTTP " + response.status);
      }
      remaining.shift();
      writePendingChunks(remaining);
      sendNext();
    }).catch(function () {
      // Keep the rest of the queue for the next page load.
      writePendingChunks(remaining);
    });
  };
  sendNext();
}
